import { AfterViewInit, Component, OnInit, ViewChild, EventEmitter, Input, Output } from '@angular/core';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatStepper } from '@angular/material/stepper';
import { MatDialog } from '@angular/material';
import { MetadataService } from '@core/services/metadata.service';
import { BasicDialogComponent } from '@modules/policy/shared/components/basic-dialog/basic-dialog.component';
import { isEffectiveDateOutsideBindingAuthority } from '@shared/rules/binding-authority.rules';
import { BillingService } from '../../services/billing.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ]
})
export class StepperComponent implements OnInit, AfterViewInit {
  billingLookups;
  header;
  private previousStepIndex = 0;
  private modalOpen = false;
  @ViewChild('stepper', { static: true }) stepper: MatStepper;

  constructor(
    private billingService: BillingService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private metadataService: MetadataService
  ) { }

  private isOutsideBindingAuthority(): boolean {
    const effectiveDate = this.header?.PolicyEffectiveDateDt;
    const maxPastDays = this.metadataService.getMetadata()?.policyCreationModal?.effectiveDate?.maxPastDays ?? 5;
    return isEffectiveDateOutsideBindingAuthority(effectiveDate, maxPastDays);
  }

  private showBindingAuthorityModal(): void {
    if (this.modalOpen) {
      return;
    }
    this.modalOpen = true;
    this.dialog.open(BasicDialogComponent, {
      disableClose: true, // Maybe remove
      width: '480px',
      data: {
        title: 'Alert',
        message: 'This submission falls outside of your binding authority. Please re-quote with a different effective date, or contact our underwriting team.',
        buttons: [{ label: 'Continue', action: true }],
        dialogPrefix: 'binding-authority-modal',
      },
    }).afterClosed().subscribe(() => {
      this.modalOpen = false;
      this.router.navigate(['/dashboard']);
    });
  }

  ngOnInit() {
    const { lookups, headerData } = this.route.snapshot.data?.billing;
    this.header = headerData;
    this.billingLookups = lookups;
  }

  ngAfterViewInit(): void {
    if (this.isOutsideBindingAuthority()) {
      this.showBindingAuthorityModal();
      return;
    }
    this.updateStep();
  }

  onStepSelectionChange(event: StepperSelectionEvent) {
    if (this.isOutsideBindingAuthority()) {
      this.stepper.selectedIndex = this.previousStepIndex;
      this.showBindingAuthorityModal();
      return;
    }
    this.previousStepIndex = event.selectedIndex;
    this.billingService.handleStepChange(event);
  }

  updateStep() {
    if (this.billingService.billingData.BillingStatusCd === 'CV') {
      this.stepper.next();
      this.stepper.next();
    } else if (this.isBillingComplete()) {
      this.stepper.next();
    }
    this.previousStepIndex = this.stepper.selectedIndex;
  }

  isBillingComplete() {
    const { billingData, PAYMENT_TYPES } = this.billingService
    if (billingData == null) return false
    
    return Boolean(
      billingData.BillingPaymentTypeCd && billingData.BillingPaymentFrequencyCd && billingData.BillingDownpaymentAmt >= 0
      && (
        billingData.BillingWithdrawalDateDt 
        || (!billingData.BillingWithdrawalDateDt && billingData.BillingPaymentTypeCd == PAYMENT_TYPES.invoice)
      ) 
    )
  }

  isPaymentComplete() {
    if (this.billingService.billingData != null) {
      return this.billingService.billingData.BillingCreditCardAuthorizationInd == true &&
        this.billingService.billingData.BillingPaymentConfirmationInd == true;
    } else {
      return false;
    }
  }

  billingStep() {
    if (this.isOutsideBindingAuthority()) {
      this.showBindingAuthorityModal();
      return;
    }
    this.stepper.previous();
    this.previousStepIndex = this.stepper.selectedIndex;
  }

  paymentStep(event) {
    if (this.isOutsideBindingAuthority()) {
      this.showBindingAuthorityModal();
      return;
    }
    this.billingService.saveBillingData(event, this.billingService.billingData.BillingPolicyID).subscribe(
      (_: any) => {
        this.billingService.calculateResourceUrls(true);
        this.stepper.next();
        this.previousStepIndex = this.stepper.selectedIndex;
      });
  }

  confirmStep() {
    if (this.isOutsideBindingAuthority()) {
      this.showBindingAuthorityModal();
      return;
    }
    this.billingService.bindPolicy(this.billingService.billingData.BillingPolicyID).subscribe(
      (_: any) => {
        this.stepper.next();
        this.previousStepIndex = this.stepper.selectedIndex;
      });
  }

  saveBillingData(event: { [s: string]: any }) {
    this.billingService.saveBillingData(event, this.billingService.billingData.BillingPolicyID).subscribe();
  }
}
